import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import Num from '../components/Num.jsx'
import { CATEGORIES, CATEGORY_KEYS, CATEGORY_SHORT as CATEGORY_LABEL, DEFAULT_CATEGORY_PATH } from '../lib/categories.js'
import { listEvents, EVENT_TYPES, relativeTime } from '../lib/events.js'
import { useManifest } from '../lib/data.jsx'
import { listIntel } from '../lib/intel.js'
import { gmailStatus } from '../lib/gmail.js'
import { enrichedSet } from '../lib/enrichedLeads.js'
import { hubspotMetrics } from '../lib/hubspot.js'
import { cacheKey, isStale, readCached, writeCached } from '../lib/metricsCache.js'
import { addDays, addMonths, chicagoDayOf, isDayKey, rateOf, sendRatesOf, weekStartOf } from '../lib/activityMetrics.js'
import { useAuth } from '../auth.jsx'

// Dashboard — the cockpit's cards and data, presented in the Fey "Analysis"
// language: big quiet title, hairline tab row, flat hairline cards (no
// shadows), generous section spacing, one dense borderless table. Sections:
// outreach activity (dual-line) + deliverability, latest warm leads. Today's
// operations, the approach queue and the pipeline shape came off the overview
// on request (2026-10-13).
//
// OUTREACH NUMBERS COME FROM HUBSPOT, for the whole workspace. They used to be
// counted from the app's own email_sent events, which nothing writes (the app
// stages drafts, it never sends) — so the chart and Deliverability read 0 over
// months of real outreach. Now they are the ledger the Activity page reads
// (/api/hubspot/metrics, compute_metrics in the hubspot lambda) with
// scope=workspace: every rep's HubSpot-logged first touches, follow-ups and
// replies sent on one line, prospects' responses on the other. Leads and warm
// accounts still come from the manifest and intel stores.

const DAY_MS = 86400000

// Chart ranges as America/Chicago day keys — the ledger's own days, so a send
// at 11pm Chicago lands on the day the rep made it, not on the UTC day.
// `since` is inclusive; null is "from the first day the ledger holds".
const RANGES = [
  { key: '7D', since: (today) => addDays(today, -6) },
  { key: '1M', since: (today) => addDays(today, -29) },
  { key: '3M', since: (today) => addMonths(today, -3) },
  { key: '6M', since: (today) => addMonths(today, -6) },
  { key: 'All', since: () => null },
]

const STAGE_TONE = {
  replied: 'bg-[#eff6ff] text-[#1d4ed8] dark:bg-[rgba(29,78,216,0.2)] dark:text-[#93bdfb]',
  meeting: 'bg-[#f5f3ff] text-[#6d28d9] dark:bg-[rgba(109,40,217,0.22)] dark:text-[#c4b5fd]',
  won: 'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(21,128,61,0.18)] dark:text-[#4ade80]',
}

const CARD = 'rounded-[14px] bg-surface p-7 [border:1px_solid_var(--table-line)]'

// In-page tabs — each panel is built from the same real stores, so switching
// never navigates away (and never snaps: the panel cross-fades in).
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'leads', label: 'Leads' },
  { key: 'events', label: 'Events' },
]

// "2:51 PM" — when the numbers on screen were read from the mirror.
function clockOf(ms) {
  const d = new Date(ms)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
function fmtDate(iso) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10)
}


function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] || '·') + (parts[1]?.[0] || '')).toUpperCase()
}

// Tiny circular badge, like the reference's row markers: P = priority/warm.
// Every row this page marks is a priority row, so there is only the one state.
function Dot() {
  return (
    <span className="grid h-[18px] w-[18px] flex-none place-items-center rounded-full bg-[rgba(34,197,94,0.16)] text-[10px] font-bold text-[#22c55e]">
      P
    </span>
  )
}

function Avatar({ name, size = 26 }) {
  return (
    <span
      className="grid flex-none place-items-center rounded-full bg-panel text-[10px] font-bold tracking-[0.02em] text-dim"
      style={{ width: size, height: size }}
    >
      {initialsOf(name)}
    </span>
  )
}

// Dual-line chart, no fills — the reference's "Daily filings" look.
function DualLine({ a, b, height = 200 }) {
  const width = 760
  const pad = { top: 12, right: 6, bottom: 8, left: 6 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const max = Math.max(1, ...a, ...b)
  const path = (pts) =>
    pts
      .map((v, i) => {
        const x = pad.left + (i / Math.max(1, pts.length - 1)) * innerW
        const y = pad.top + innerH - (v / max) * innerH
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  // Both lines rise from the baseline together (same origin trick as the bars).
  const rise = '[transform-box:fill-box] [transform-origin:bottom] animate-[barGrow_0.6s_cubic-bezier(0.22,1,0.36,1)_both]'
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="block h-auto w-full" role="img" aria-label="Sent and replied over time">
      <path d={path(b)} fill="none" stroke="var(--primary)" strokeWidth="1.6" strokeLinejoin="round" className={rise} />
      <path d={path(a)} fill="none" stroke="var(--text)" strokeWidth="1.6" strokeLinejoin="round" opacity="0.9" className={rise} />
    </svg>
  )
}

// The chart's two lines over [sinceDay .. today], one point per Chicago day,
// cut from the ledger's per-day rows: sent = every outbound email HubSpot
// holds (first touches, follow-ups, replies sent); replied = prospects'
// responses. A day with no row is a real zero — the read covers the whole
// mirror, so a missing day is a day nobody emailed.
function seriesFrom(dayRows, sinceDay, today) {
  const rows = Array.isArray(dayRows) ? dayRows : []
  const byDay = new Map(rows.map((d) => [d.day, d]))
  let first = sinceDay || (rows.length ? rows[0].day : today)
  if (!isDayKey(first) || first > today) first = today
  const sent = []
  const replied = []
  for (let day = first; day <= today; day = addDays(day, 1)) {
    const d = byDay.get(day)
    sent.push(d ? (d.outreach_emails || 0) + (d.followups || 0) + (d.replies || 0) : 0)
    replied.push(d ? d.responses || 0 : 0)
  }
  return {
    sent,
    replied,
    sentTotal: sent.reduce((s, n) => s + n, 0),
    repliedTotal: replied.reduce((s, n) => s + n, 0),
  }
}

// A quiet labelled row inside the flat cards (deliverability etc.).
// "38% · 42% all time": this week's rate leading, all time beside it in the
// note's weight. A missing side is a dash, never 0%.
function RatePair({ week, all, loading }) {
  if (week == null && all == null) return loading ? '…' : '—'
  return (
    <>
      {week == null ? '—' : <Num value={week} format={(n) => `${Math.round(n)}%`} />}
      <span className="ml-1.5 text-[12.5px] font-medium text-faint">· {all == null ? '—' : `${Math.round(all)}%`} all time</span>
    </>
  )
}

function StatRow({ label, children }) {
  return (
    <div className="flex items-center justify-between py-[11px] [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
      <span className="text-[13px] text-dim">{label}</span>
      <span className="text-[13.5px] font-semibold tabular-nums text-ink">{children}</span>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [tab, setTab] = useState('overview')
  const [rangeKey, setRangeKey] = useState('1M')
  const [allEvents, setAllEvents] = useState([])
  const [gmail, setGmail] = useState(null)
  // The workspace's HubSpot ledger, read once: null while loading, then the
  // /metrics body (`available` / `complete` say whether it can be trusted).
  const { user } = useAuth()
  const [metrics, setMetrics] = useState(null)

  useEffect(() => {
    let cancelled = false
    listEvents({ limit: 500 }).then(({ events }) => {
      if (cancelled) return
      setAllEvents(events.filter((e) => e.createdAt))
    })
    gmailStatus().then((s) => {
      if (!cancelled) setGmail(s)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // One all-time read; every chart range and the Deliverability rows are cut
  // from its per-day rows here, so they can never disagree with each other.
  // `rows: 'deals'` keeps the payload to the counts (the route always sends at
  // least one row list, and deals is the smallest).
  //
  // NOT ON EVERY LOAD. The read costs the mirror ~10 s, so the body is kept in
  // IndexedDB under (actor, scope, Chicago day) — lib/metricsCache.js — and a
  // load paints from today's row at once. The mirror is read again only when
  // that row is older than REFRESH_AFTER_MS (in the background, the cached
  // numbers staying up until the fresh ones land), on the first load of a new
  // day, or when Refresh is pressed. A background read that fails keeps the
  // cached numbers rather than blanking them.
  const actorEmail = String(user?.email || '').toLowerCase()
  const todayDay = chicagoDayOf(Date.now())
  const [readAt, setReadAt] = useState(null) // when the numbers on screen left the mirror
  const [refreshing, setRefreshing] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)
  const metricsRef = useRef(null)
  metricsRef.current = metrics
  useEffect(() => {
    if (!actorEmail) return undefined
    let cancelled = false
    const key = cacheKey({ actor: actorEmail, scope: 'workspace', day: todayDay })
    const readMirror = async (background) => {
      if (!background) setMetrics(null)
      setRefreshing(true)
      const m = await hubspotMetrics({ actor: actorEmail, scope: 'workspace', rows: 'deals', density_bucket: 'day' })
      if (cancelled) return
      setRefreshing(false)
      const good = Boolean(m && m.available && m.complete)
      if (background && !good) return
      setMetrics(m)
      if (good) {
        setReadAt(Date.now())
        writeCached(key, todayDay, m)
      }
    }
    ;(async () => {
      const cached = refreshTick ? null : await readCached(key)
      if (cancelled) return
      if (cached) {
        setMetrics(cached.body)
        setReadAt(cached.savedAt)
        if (isStale(cached.savedAt)) readMirror(true)
      } else {
        readMirror(Boolean(metricsRef.current))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [actorEmail, todayDay, refreshTick])

  // Database line — real manifest counts.
  const { manifest } = useManifest()
  const db = useMemo(() => {
    const cats = manifest?.categories ?? {}
    const sum = (arr) => (arr || []).reduce((s, r) => s + (r.count || 0), 0)
    // One row per vertical in lib/categories.js, plus the combined total.
    const byCategory = CATEGORIES.map((c) => ({ ...c, count: sum(cats[c.key]) }))
    return { byCategory, total: byCategory.reduce((s, c) => s + c.count, 0) }
  }, [manifest])

  // The ledger, if it answered. /metrics says complete:false (not computed)
  // for a failed read, and that shows as unavailable — never as zeros.
  const ledgerOk = Boolean(metrics && metrics.available && metrics.complete)
  const ledgerLoading = metrics === null
  const ledgerReason = metrics && !ledgerOk ? metrics.reason || 'The HubSpot mirror could not be read.' : ''

  // Chart series: sent (ink) vs replies (primary) per Chicago day over the range.
  const series = useMemo(() => {
    const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1]
    return seriesFrom(ledgerOk ? metrics.days : [], range.since(todayDay), todayDay)
  }, [metrics, ledgerOk, rangeKey, todayDay])

  // Deliverability: this week's sends and replies (Monday-start, week to date
  // — the Activity page's "week") and the ALL-TIME reply rate, companies that
  // answered over companies emailed. All-time on both sides on purpose: a
  // range-limited rate compares different cohorts and flatters.
  const delivery = useMemo(() => {
    if (!ledgerOk) return null
    const week = seriesFrom(metrics.days, weekStartOf(todayDay), todayDay)
    const reached = metrics.all_time?.outreach_companies || 0
    const answered = metrics.all_time?.responding_companies || 0
    // Delivery and open rates (2026-10-20): this week summed from the raw
    // per-day counts and divided once, all time straight off the server's
    // totals. `sendsKnown` is false against a mirror lambda (or a cached
    // answer) from before the sends ledger — dashes then, never 0%.
    const weekStart = weekStartOf(todayDay)
    const weekSends = sendRatesOf((metrics.days || []).filter((d) => d && d.day >= weekStart && d.day <= todayDay))
    const sendsKnown = Boolean(metrics.all_time && Object.prototype.hasOwnProperty.call(metrics.all_time, 'emails_sent'))
    return {
      sentThisWeek: week.sentTotal,
      repliesThisWeek: week.repliedTotal,
      reached,
      answered,
      replyRatePct: reached ? (answered / reached) * 100 : null,
      sendsKnown,
      deliveryWeekPct: sendsKnown && weekSends.deliveryRate != null ? weekSends.deliveryRate * 100 : null,
      openWeekPct: sendsKnown && weekSends.openRate != null ? weekSends.openRate * 100 : null,
      deliveryAllPct: sendsKnown && rateOf(metrics.all_time, 'delivery_rate') != null ? rateOf(metrics.all_time, 'delivery_rate') * 100 : null,
      openAllPct: sendsKnown && rateOf(metrics.all_time, 'open_rate') != null ? rateOf(metrics.all_time, 'open_rate') * 100 : null,
      bouncedAll: sendsKnown ? metrics.all_time.emails_bounced || 0 : null,
    }
  }, [metrics, ledgerOk, todayDay])

  // Newest warm leads — the dense table.
  const warm = useMemo(
    () =>
      listIntel()
        .filter((r) => ['replied', 'meeting', 'won'].includes(r.stage))
        .map((r) => ({ ...r, lastAt: (r.outcomes || []).slice(-1)[0]?.at || r.firstSeenAt }))
        .sort((a, b) => String(b.lastAt).localeCompare(String(a.lastAt)))
        .slice(0, 8),
    [],
  )

  // ── Leads tab: database shape from the manifest + what this workspace tracks
  const leadsModel = useMemo(() => {
    const cats = manifest?.categories ?? {}
    const byLabel = new Map()
    for (const key of CATEGORY_KEYS) {
      for (const r of cats[key] ?? []) {
        byLabel.set(r.label, (byLabel.get(r.label) || 0) + (r.count || 0))
      }
    }
    const markets = [...byLabel.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
    const intel = listIntel()
    return {
      markets: markets.slice(0, 6),
      marketCount: byLabel.size,
      tracked: intel.length,
      enriched: enrichedSet().size,
      // Every warm account, not the eight the overview table shows.
      warm: intel.filter((r) => ['replied', 'meeting', 'won'].includes(r.stage)).length,
    }
  }, [manifest])

  // ── Events tab: the real activity log, newest first + this week's mix
  const eventsModel = useMemo(() => {
    const weekAgo = Date.now() - 7 * DAY_MS
    const week = allEvents.filter((e) => new Date(e.createdAt).getTime() >= weekAgo)
    const byType = new Map()
    for (const e of week) byType.set(e.type, (byType.get(e.type) || 0) + 1)
    return {
      recent: allEvents.slice(0, 12),
      weekTotal: week.length,
      mix: [...byType.entries()]
        .map(([type, n]) => ({ type, label: EVENT_TYPES[type]?.label || type, n }))
        .sort((a, b) => b.n - a.n)
        .slice(0, 6),
    }
  }, [allEvents])

  // One underline that slides between tabs: measure the active button and
  // translate/resize the indicator, so switching glides instead of blinking.
  const tabRowRef = useRef(null)
  const tabRefs = useRef(new Map())
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false })

  useLayoutEffect(() => {
    function measure() {
      const el = tabRefs.current.get(tab)
      const row = tabRowRef.current
      if (!el || !row) return
      const a = el.getBoundingClientRect()
      const b = row.getBoundingClientRect()
      setIndicator({ left: a.left - b.left, width: a.width, ready: true })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [tab])

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })



  return (
    <div className="mx-auto w-full max-w-[1180px] px-2 pb-16">
      {/* ── Title ─────────────────────────────────────────────────────────── */}
      {/* One action, because there is only one: every operation this page
          nudges is run by the agent in chat. */}
      <div className="flex items-start justify-between pt-4">
        <div>
          <h1 className="m-0 text-[30px] font-bold tracking-[-0.02em] text-ink">Dashboard</h1>
          <div className="mt-1.5 text-[15px] text-dim">{today}</div>
        </div>
        <NavLink
          to="/chat"
          className="mt-1 inline-flex items-center gap-x-2 rounded-[10px] bg-primary px-4 py-[9px] text-[13.5px] font-semibold text-white no-underline transition-colors hover:bg-primary-dark"
        >
          Open chat
        </NavLink>
      </div>

      {/* ── Quiet tab row + database line ─────────────────────────────────── */}
      {/* The counts line rides the tab row on desktop; on a phone it would push
          the tabs off-screen, so it sits under them instead. */}
      <div
        ref={tabRowRef}
        className="relative mt-9 flex items-center gap-x-9 [border-bottom:1px_solid_var(--table-line)] max-md:mt-6 max-md:gap-x-6 max-md:overflow-x-auto max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            ref={(el) => (el ? tabRefs.current.set(t.key, el) : tabRefs.current.delete(t.key))}
            onClick={() => setTab(t.key)}
            aria-current={tab === t.key ? 'page' : undefined}
            className={`relative border-none bg-transparent px-0 pb-3 text-[14px] font-medium transition-colors duration-300 ${
              tab === t.key ? 'text-ink' : 'text-faint hover:text-dim'
            }`}
          >
            {t.label}
          </button>
        ))}
        {/* Sliding underline — glides to the active tab. */}
        <span
          aria-hidden="true"
          className="absolute -bottom-px left-0 h-[2px] rounded-full bg-ink transition-[transform,width] duration-300 ease-out"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            opacity: indicator.ready ? 1 : 0,
          }}
        />
        <span className="ml-auto whitespace-nowrap pb-3 text-[12.5px] text-faint max-md:hidden">
          {db.total > 0 && (
            <>
              <span className="font-semibold text-dim"><Num value={db.total} /></span> verified leads
              {/* The per-vertical split only says something once there are two. */}
              {db.byCategory.length > 1 &&
                db.byCategory.map((c) => (
                  <span key={c.key}>
                    {' · '}
                    <span className="font-semibold text-dim"><Num value={c.count} /></span> {c.label}
                  </span>
                ))}
            </>
          )}
        </span>
      </div>

      {/* Panels cross-fade: the key restarts the animation on every switch. */}
      <div key={tab} className="animate-[tabIn_0.28s_cubic-bezier(0.22,1,0.36,1)]">
      {tab === 'overview' && (
      <>
      {/* ── Row A: activity chart + deliverability ────────────────────────── */}
      <section className="mt-12 grid grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] gap-10 max-[1100px]:grid-cols-1">
        <div className={CARD}>
          <div className="flex items-baseline justify-between">
            <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">Outreach activity</h2>
            <span className="flex items-center gap-x-4 text-[12.5px] text-dim">
              <span className="inline-flex items-center gap-x-1.5">
                <span className="h-[7px] w-[7px] rounded-full bg-[color:var(--text)]" /> Sent{' '}
                {ledgerOk ? <Num value={series.sentTotal} /> : ledgerLoading ? '…' : '—'}
              </span>
              <span className="inline-flex items-center gap-x-1.5">
                <span className="h-[7px] w-[7px] rounded-full bg-primary" /> Replies{' '}
                {ledgerOk ? <Num value={series.repliedTotal} /> : ledgerLoading ? '…' : '—'}
              </span>
            </span>
          </div>
          <div className="mt-4">
            {ledgerOk ? (
              <DualLine a={series.sent} b={series.replied} />
            ) : (
              <div className="grid h-[200px] place-items-center text-[13px] text-faint">
                {ledgerLoading ? 'Reading the HubSpot mirror…' : ledgerReason}
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-x-1">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRangeKey(r.key)}
                  className={`rounded-[7px] border-none bg-transparent px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors ${
                    rangeKey === r.key ? 'text-ink' : 'text-faint hover:text-dim'
                  }`}
                >
                  {r.key}
                </button>
              ))}
            </div>
            <span className="inline-flex items-center gap-x-2 text-[12px] text-faint">
              {ledgerOk
                ? `HubSpot · read ${readAt ? clockOf(readAt) : 'just now'}${refreshing ? ' · refreshing…' : ''}`
                : ledgerLoading
                  ? 'Reading HubSpot…'
                  : 'HubSpot unavailable'}
              {!ledgerLoading && !refreshing && (
                <button
                  type="button"
                  onClick={() => setRefreshTick((t) => t + 1)}
                  className="cursor-pointer border-none bg-transparent p-0 text-[12px] font-semibold text-dim transition-colors hover:text-ink"
                >
                  Refresh
                </button>
              )}
            </span>
          </div>
        </div>

        <div className={CARD}>
          <div className="flex items-baseline justify-between">
            <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">Deliverability</h2>
            <NavLink to="/settings" className="text-[13px] font-medium text-faint no-underline transition-colors hover:text-ink">
              Settings
            </NavLink>
          </div>
          <div className="mt-4 flex flex-col">
            <StatRow label="Gmail connection">
              {gmail === null ? '…' : gmail.connected ? (
                <span className="text-[#22c55e]">Connected</span>
              ) : (
                <NavLink to="/settings" className="text-primary-dark no-underline hover:underline">
                  Connect
                </NavLink>
              )}
            </StatRow>
            <StatRow label="Sent this week">
              {delivery ? <Num value={delivery.sentThisWeek} /> : ledgerLoading ? '…' : '—'}
            </StatRow>
            <StatRow label="Replies this week">
              {delivery ? <Num value={delivery.repliesThisWeek} /> : ledgerLoading ? '…' : '—'}
            </StatRow>
            <StatRow label="Reply rate · all time">
              {delivery && delivery.replyRatePct != null ? (
                <Num value={delivery.replyRatePct} format={(n) => `${Math.round(n)}%`} />
              ) : ledgerLoading ? (
                '…'
              ) : (
                '—'
              )}
            </StatRow>
            <StatRow label="Delivery rate · this week">
              <RatePair week={delivery && delivery.deliveryWeekPct} all={delivery && delivery.deliveryAllPct} loading={ledgerLoading} />
            </StatRow>
            <StatRow label="Open rate · this week">
              <RatePair week={delivery && delivery.openWeekPct} all={delivery && delivery.openAllPct} loading={ledgerLoading} />
            </StatRow>
          </div>
          {/* Bounces ARE read back now (2026-10-20): HubSpot marks a send
              BOUNCED with the receiving server's reason, and opens come off its
              tracker — Bo's Gmail-connected sends today, Apollo's sequence
              emails once Apollo logs them in. Before that ledger existed a
              permanent "0 bounced" would have been a claim, not a gap. */}
          <p className="m-0 mt-4 text-[12px] leading-[1.5] text-faint">
            {delivery
              ? `Emails logged in HubSpot by everyone in the workspace, synced every five minutes. Reply rate is all-time: ${delivery.answered.toLocaleString()} of ${delivery.reached.toLocaleString()} companies emailed have answered.${
                  delivery.sendsKnown
                    ? ` Delivery and open rates come from HubSpot's tracker (${(delivery.bouncedAll || 0).toLocaleString()} bounced all time); opens run high where Apple Mail pre-loads images.`
                    : ''
                }`
              : ledgerLoading
                ? 'Reading the HubSpot mirror…'
                : ledgerReason}
          </p>
        </div>
      </section>

      {/* ── Dense table ───────────────────────────────────────────────────── */}
      <h2 className="mb-0 mt-14 text-[21px] font-bold tracking-[-0.015em] text-ink">Latest warm leads</h2>
      <div className="mt-6">
        {warm.length === 0 ? (
          <p className="m-0 rounded-[12px] py-10 text-center text-[13.5px] text-faint [border:1px_dashed_var(--table-line)]">
            No replies logged yet — when a lead responds, log the outcome on their page and they collect here.
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left text-[12.5px] font-medium text-faint">
                <th className="pb-3 pl-1 font-medium">Company name</th>
                <th className="pb-3 font-medium">Stage</th>
                <th className="pb-3 font-medium">Type</th>
                <th className="pb-3 text-right font-medium">Date</th>
                <th className="pb-3 text-right font-medium">Score</th>
                <th className="pb-3 pr-1" />
              </tr>
            </thead>
            <tbody>
              {warm.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer transition-colors [border-top:1px_solid_var(--table-line)] hover:bg-panel"
                  onClick={() =>
                    navigate(`/leads/company/${encodeURIComponent(r.id)}`, {
                      state: { company: { company_id: r.id, name: r.name }, category: r.category || undefined },
                    })
                  }
                >
                  <td className="py-[13px] pl-1">
                    <span className="flex items-center gap-x-3">
                      <Avatar name={r.name} />
                      <span className="truncate text-[13.5px] font-semibold text-ink">{r.name}</span>
                    </span>
                  </td>
                  <td className="py-[13px]">
                    <span
                      className={`inline-flex rounded-[6px] px-2.5 py-[3px] text-[12px] font-semibold capitalize ${
                        STAGE_TONE[r.stage] || 'bg-panel text-dim'
                      }`}
                    >
                      {r.stage}
                    </span>
                  </td>
                  <td className="py-[13px] text-[13px] text-dim">{CATEGORY_LABEL[r.category] || '—'}</td>
                  <td className="py-[13px] text-right text-[13px] tabular-nums text-dim">{fmtDate(r.lastAt)}</td>
                  <td className="py-[13px] text-right text-[13.5px] font-semibold tabular-nums text-ink">
                    {r.attrs?.base != null ? Math.round(r.attrs.base) : '—'}
                  </td>
                  <td className="py-[13px] pr-1 text-right">
                    <span className="inline-flex justify-end">
                      <Dot />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}

      {/* ── Leads ─────────────────────────────────────────────────────────── */}
      {tab === 'leads' && (
        <>
          <section className="mt-12 grid grid-cols-[minmax(0,1fr)_minmax(0,1.45fr)] gap-10 max-[1100px]:grid-cols-1">
            <div className={CARD}>
              <div className="flex items-baseline justify-between">
                <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">Lead database</h2>
                <NavLink to={DEFAULT_CATEGORY_PATH} className="text-[13px] font-medium text-faint no-underline transition-colors hover:text-ink">
                  Browse →
                </NavLink>
              </div>
              <div className="mt-4 text-[40px] font-bold leading-none tracking-[-0.03em] text-ink">
                {db.total ? <Num value={db.total} duration={1100} /> : '…'}
              </div>
              <div className="mt-1.5 text-[13px] text-dim">verified companies, ready to work</div>
              <div className="mt-5 flex flex-col">
                {db.byCategory.map((c) => (
                  <StatRow key={c.key} label={c.plural}><Num value={c.count} /></StatRow>
                ))}
                <StatRow label="Markets covered"><Num value={leadsModel.marketCount} /></StatRow>
              </div>
            </div>

            <div className={CARD}>
              <div className="flex items-baseline justify-between">
                <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">Largest markets</h2>
              </div>
              {leadsModel.markets.length ? (
                <div className="mt-5 flex flex-col">
                  {leadsModel.markets.map((m) => (
                    <div
                      key={m.label}
                      className="flex items-center gap-x-4 py-[10px] [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
                    >
                      <span className="min-w-0 flex-1 truncate text-[13.5px] text-dim">{m.label}</span>
                      <span className="h-[6px] w-[120px] flex-none overflow-hidden rounded-full bg-panel">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ width: `${Math.round((m.value / leadsModel.markets[0].value) * 100)}%` }}
                        />
                      </span>
                      <span className="w-[64px] flex-none text-right text-[13px] font-semibold tabular-nums text-ink">
                        <Num value={m.value} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="m-0 py-8 text-center text-[13px] text-faint">Loading markets…</p>
              )}
            </div>
          </section>

          {/* One card, so no grid: a lone child left in a two-column track
              would sit in half the width for no reason. */}
          <section className="mt-10">
            <div className={CARD}>
              <div className="flex items-baseline justify-between">
                <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">What you're working</h2>
                <span className="text-[12.5px] text-faint">from the leads you've opened</span>
              </div>
              <div className="mt-4 flex flex-col">
                <StatRow label="Tracked accounts"><Num value={leadsModel.tracked} /></StatRow>
                <StatRow label="AI-enriched leads"><Num value={leadsModel.enriched} /></StatRow>
                <StatRow label="Warm leads"><Num value={leadsModel.warm} /></StatRow>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── Events ────────────────────────────────────────────────────────── */}
      {tab === 'events' && (
        <>
          <section className="mt-12 grid grid-cols-[minmax(0,1fr)_minmax(0,1.45fr)] gap-10 max-[1100px]:grid-cols-1">
            <div className={CARD}>
              <div className="flex items-baseline justify-between">
                <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">This week</h2>
                <NavLink to="/logs/events" className="text-[13px] font-medium text-faint no-underline transition-colors hover:text-ink">
                  Full log →
                </NavLink>
              </div>
              <div className="mt-4 text-[40px] font-bold leading-none tracking-[-0.03em] text-ink">
                <Num value={eventsModel.weekTotal} />
              </div>
              <div className="mt-1.5 text-[13px] text-dim">workspace events in the last 7 days</div>
              {eventsModel.mix.length ? (
                <div className="mt-5 flex flex-col">
                  {eventsModel.mix.map((m) => (
                    <StatRow key={m.type} label={m.label}>
                      <Num value={m.n} />
                    </StatRow>
                  ))}
                </div>
              ) : (
                <p className="m-0 mt-5 text-[13px] text-faint">Nothing logged this week yet.</p>
              )}
            </div>

            <div className={CARD}>
              <div className="flex items-baseline justify-between">
                <h2 className="m-0 text-[17px] font-bold tracking-[-0.01em] text-ink">Recent activity</h2>
                <span className="text-[12.5px] text-faint">newest first</span>
              </div>
              {eventsModel.recent.length ? (
                <div className="mt-5 flex flex-col">
                  {eventsModel.recent.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-start gap-x-3 py-[12px] [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
                    >
                      <span className="mt-[5px] h-[7px] w-[7px] flex-none rounded-full bg-primary" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13.5px] font-semibold text-ink">
                          {EVENT_TYPES[e.type]?.label || e.type}
                        </span>
                        {e.detail && (
                          <span className="mt-0.5 block truncate text-[12.5px] text-dim">{e.detail}</span>
                        )}
                      </span>
                      <span className="flex-none text-[12.5px] tabular-nums text-faint">
                        {relativeTime(e.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="m-0 py-8 text-center text-[13px] text-faint">
                  No activity yet — actions you take across the workspace show up here.
                </p>
              )}
            </div>
          </section>
        </>
      )}
      </div>
    </div>
  )
}
